import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal, Space, Typography, message } from 'antd';
import { DownloadOutlined, ExportOutlined, FullscreenExitOutlined, FullscreenOutlined } from '@ant-design/icons';
import { dccDownloadUrl, dccPreviewPdfUrl, dccUpload, type DccStorageRoot } from '../../services/dccApi';
import 'react-resizable/css/styles.css';
import PdfViewer from './PdfViewer';

const { Text } = Typography;

export type FilePreviewType = 'pdf' | 'image' | 'text' | 'unsupported';

export interface FilePreviewModalProps {
  open: boolean;
  path: string | null;
  token?: string | null;
  storageRoot?: DccStorageRoot;
  onClose: () => void;
  width?: number | string;
  enableAnnotatedUpload?: boolean;
  onAnnotatedUploaded?: (args: { filename: string; response: any; targetPath: string }) => void;
}

export const getFilePreviewType = (path: string): FilePreviewType => {
  const name = path.toLowerCase();
  if (name.endsWith('.pdf')) return 'pdf';
  if (/\.(png|jpe?g|gif|bmp|webp|svg)$/i.test(name)) return 'image';
  if (/\.(txt|log|md)$/i.test(name)) return 'text';
  return 'unsupported';
};

export const canPreviewFile = (path: string): boolean => getFilePreviewType(path) !== 'unsupported';

export const getPreviewSupportMap = (paths: string[]): Record<string, boolean> => {
  const out: Record<string, boolean> = {};
  (paths || []).forEach((p) => {
    out[p] = canPreviewFile(p);
  });
  return out;
};

const fileNameFromPath = (path: string) => path.split('/').pop() || path;

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

const resolveInitialWidthPx = (w: number | string | undefined) => {
  const fallback = 900;
  if (typeof window === 'undefined') return typeof w === 'number' ? w : fallback;
  if (typeof w === 'number') return w;
  const raw = String(w || '').trim();
  if (!raw) return fallback;

  const vw = window.innerWidth || 1200;
  if (raw.endsWith('%')) {
    const p = Number.parseFloat(raw.slice(0, -1));
    if (!Number.isFinite(p)) return fallback;
    return Math.round((vw * p) / 100);
  }
  if (raw.endsWith('vw')) {
    const p = Number.parseFloat(raw.slice(0, -2));
    if (!Number.isFinite(p)) return fallback;
    return Math.round((vw * p) / 100);
  }
  if (raw.endsWith('px')) {
    const px = Number.parseFloat(raw.slice(0, -2));
    return Number.isFinite(px) ? Math.round(px) : fallback;
  }
  const asNumber = Number.parseFloat(raw);
  return Number.isFinite(asNumber) ? Math.round(asNumber) : fallback;
};

const resolveInitialHeightPx = () => {
  if (typeof window === 'undefined') return 700;
  const vh = window.innerHeight || 900;
  return Math.round(vh * 0.82);
};

const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  open,
  path,
  token,
  storageRoot,
  onClose,
  width = 900,
  enableAnnotatedUpload,
  onAnnotatedUploaded,
}) => {
  const title = path ? fileNameFromPath(path) : 'Preview';
  const previewType = path ? getFilePreviewType(path) : 'unsupported';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isUploadingAnnotated, setIsUploadingAnnotated] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [size, setSize] = useState<{ width: number; height: number }>({
    width: resolveInitialWidthPx(width),
    height: resolveInitialHeightPx(),
  });
  const [textContent, setTextContent] = useState<string | null>(null);
  const [isTextLoading, setIsTextLoading] = useState(false);

  // Reset modal geometry and text preview state when opening a new preview
  useEffect(() => {
    if (!open) return;
    setIsFullscreen(false);
    setSize({
      width: resolveInitialWidthPx(width),
      height: resolveInitialHeightPx(),
    });
    setTextContent(null);
    setIsTextLoading(false);
  }, [open, width, path]);

  // Lazy-load text content when previewing text files
  useEffect(() => {
    if (!open || !path) return;
    if (previewType !== 'text') return;

    let cancelled = false;
    const load = async () => {
      try {
        setIsTextLoading(true);
        const url = dccDownloadUrl(path, token || null, true, storageRoot);
        const resp = await fetch(url);
        if (!resp.ok) {
          throw new Error(`Failed to load text content (status ${resp.status})`);
        }
        const txt = await resp.text();
        if (!cancelled) {
          setTextContent(txt);
        }
      } catch (err: any) {
        if (!cancelled) {
          setTextContent(null);
          message.error(err?.message || 'Failed to load text content');
        }
      } finally {
        if (!cancelled) {
          setIsTextLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, path, previewType, storageRoot, token]);

  const handleUploadAnnotatedClick = () => {
    fileInputRef.current?.click();
  };

  const handleAnnotatedFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const user = localStorage.getItem('user');
      if (!user) throw new Error('No user found in localStorage.');
      const targetPath = `temp/${user}`;
      setIsUploadingAnnotated(true);
      const resp = await dccUpload(targetPath, [f], token);
      const serverFilename = (() => {
        const uploaded = (resp as any)?.uploaded;
        if (Array.isArray(uploaded) && uploaded.length > 0) return String(uploaded[0]);
        const fn = (resp as any)?.filename;
        if (fn) return String(fn);
        return f.name;
      })();
      message.success('Annotated copy uploaded');
      onAnnotatedUploaded?.({ filename: serverFilename, response: resp, targetPath });
    } catch (err: any) {
      message.error(err?.message || 'Failed to upload annotated copy');
    } finally {
      setIsUploadingAnnotated(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const canResize = open && !isFullscreen;
  const constraints =
    typeof window === 'undefined'
      ? {
          minW: 520,
          minH: 360,
          maxW: 1600,
          maxH: 1000,
        }
      : {
          minW: 520,
          minH: 360,
          maxW: Math.max(560, (window.innerWidth || 1200) - 24),
          maxH: Math.max(420, (window.innerHeight || 900) - 24),
        };

  const contentHeight = useMemo(() => {
    if (typeof window === 'undefined') return 520;
    if (isFullscreen) return Math.round((window.innerHeight || 900) * 0.85);
    // Very small padding for header/body and optional upload section (tight fit)
    const chrome = enableAnnotatedUpload ? 50 : 35;
    return clamp(size.height - chrome, 200, constraints.maxH - 24);
  }, [constraints.maxH, enableAnnotatedUpload, isFullscreen, size.height]);

  const previewUrl =
    !path ? null : previewType === 'pdf'
      ? dccPreviewPdfUrl(path, token || null, storageRoot)
      : dccDownloadUrl(path, token || null, true, storageRoot);
  const downloadUrl = !path ? null : dccDownloadUrl(path, token || null, false, storageRoot);

  const handleResizeMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isFullscreen) return; // don't resize when fullscreen
    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = size.width;
    const startHeight = size.height;

    const onMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const nextW = clamp(startWidth + dx, constraints.minW, constraints.maxW);
      const nextH = clamp(startHeight + dy, constraints.minH, constraints.maxH);
      setSize({ width: nextW, height: nextH });
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        maskClosable={false}
        width={isFullscreen ? '100vw' : size.width}
        destroyOnClose
        style={isFullscreen ? { top: 0, paddingBottom: 0 } : { top: 24 }}
        title={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            // Reserve space for Modal close (X); otherwise Download aligns under it
            paddingInlineEnd: 56,
          }}
        >
          <Text ellipsis style={{ maxWidth: 'min(55vw, calc(100% - 220px))' }} title={title}>
            {title}
          </Text>
          <Space size={6}>
            <Button
              size="small"
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={() => setIsFullscreen((v) => !v)}
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            />
            <Button
              size="small"
              icon={<ExportOutlined />}
              disabled={!previewUrl}
              onClick={() => {
                if (!previewUrl) return;
                window.open(previewUrl, '_blank', 'noopener,noreferrer');
              }}
              title="Open in new tab"
            />
            <Button
              size="small"
              icon={<DownloadOutlined />}
              disabled={!downloadUrl}
              onClick={() => {
                if (!downloadUrl) return;
                window.open(downloadUrl, '_blank', 'noopener,noreferrer');
              }}
              title="Download"
            />
          </Space>
        </div>
        }
      >
        <div
          ref={containerRef}
          style={{ position: 'relative', width: '100%', height: isFullscreen ? 'auto' : size.height, overflow: 'hidden' }}
        >
          {previewType === 'pdf' && enableAnnotatedUpload ? (
            <div style={{ marginBottom: 12 }}>
              <Space>
                <Button
                  type="primary"
                  onClick={handleUploadAnnotatedClick}
                  loading={isUploadingAnnotated}
                  disabled={isUploadingAnnotated}
                >
                  Upload annotated copy
                </Button>
                <Typography.Text type="secondary">
                  Save annotations in the PDF viewer first, then upload the saved file.
                </Typography.Text>
              </Space>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                style={{ display: 'none' }}
                onChange={handleAnnotatedFileSelected}
              />
            </div>
          ) : null}
          {!path ? null : previewType === 'pdf' ? (
            <PdfViewer fileUrl={dccPreviewPdfUrl(path, token || null, storageRoot)} height={contentHeight} />
          ) : previewType === 'image' ? (
            <div style={{ width: '100%', textAlign: 'center' }}>
              <img
                src={dccDownloadUrl(path, token || null, true, storageRoot)}
                alt={title}
                style={{ maxWidth: '100%', maxHeight: contentHeight, objectFit: 'contain' }}
              />
            </div>
          ) : previewType === 'text' ? (
            <div
              style={{
                padding: 12,
                maxHeight: contentHeight,
                overflow: 'auto',
                backgroundColor: '#fff',
                color: 'rgba(0,0,0,0.85)',
                border:  '1px solid #f0f0f0',
                borderRadius: 4,
                fontFamily: 'monospace',
                fontSize: 12,
                whiteSpace: 'pre-wrap',
              }}
            >
              {isTextLoading && !textContent ? (
                <Text type="secondary">Loading text content...</Text>
              ) : textContent != null ? (
                textContent
              ) : (
                <Text type="secondary">No text content available.</Text>
              )}
            </div>
          ) : (
            <div style={{ padding: 16 }}>
              <Text type="secondary">Preview is available for PDF, image, and text files only.</Text>
            </div>
          )}
          {!isFullscreen && (
            <div
              onMouseDown={handleResizeMouseDown}
              style={{
                position: 'absolute',
                width: 18,
                height: 18,
                right: 4,
                bottom: 4,
                cursor: 'nwse-resize',
                background: 'rgba(0, 0, 0, 0.25)',
                borderRadius: 4,
                boxShadow: '0 0 3px rgba(0, 0, 0, 0.4)',
                zIndex: 10,
              }}
            />
          )}
        </div>
      </Modal>
    </>
  );
};

export default FilePreviewModal;
